import { BaseFormatAdapter } from 'app/model/adapters/dataflow/formatAdapters/base-format.adapter';
import { SapODataSettings } from 'app/model/dataflow/dataset-settings/sap-odata-settings';
import { DataFlowDSLAdapterRegistry, Metamodel, Owned, stringIsNullOrEmpty, ReadBehavior } from 'shell-models-lib';
import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';
import { type DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { IntegerDataFlowType } from 'worker/app-workers/shared/models/dataflow-datatype';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(SapODataSettings)
})
export class SapOdataAdapter extends BaseFormatAdapter<SapODataSettings> {
    protected async toSourceDSL(sapODataSettings: SapODataSettings): Promise<string[]> {
        const props = [`store: '${sapODataSettings.getFormat()}'`, `format: 'sapODataObject'`, 'staged: true'];
        if (sapODataSettings.servicePath.expression) {
            props.push(
                `servicePath: ${DataFlowExpressionUtils.wrapExpressionConfigValue(sapODataSettings.servicePath)}`
            );
        }
        if (sapODataSettings.entityName.expression) {
            props.push(`entityName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(sapODataSettings.entityName)}`);
        }
        props.push(`readMode: ${DataFlowExpressionUtils.wrapExpressionConfigValue(sapODataSettings.readMode)}`);
        if (!stringIsNullOrEmpty(sapODataSettings.batchSize.expression)) {
            props.push(
                `batchSize: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    sapODataSettings.batchSize,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        if (!stringIsNullOrEmpty(sapODataSettings.timeout.expression)) {
            props.push(
                `httpRequestTimeout: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    sapODataSettings.timeout,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        if (sapODataSettings.runMode !== ReadBehavior.FULL_ON_EVERY_RUN) {
            props.push(
                `keys: ${DataFlowExpressionUtils.wrapExpressionArrayConfigValue(sapODataSettings.sapODataKeyColumns)}`
            );
        }
        return props;
    }

    protected async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<SapODataSettings> {
        const result = Owned.create<SapODataSettings>(SapODataSettings.MODEL, owner);
        result.initialize(undefined);
        if (configs.has('servicePath')) {
            handledConfigs.push('servicePath');
            result.servicePath = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('servicePath'), owner);
        }
        if (configs.has('entityName')) {
            handledConfigs.push('entityName');
            result.entityName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('entityName'), owner);
        }
        if (configs.has('readMode')) {
            handledConfigs.push('readMode');
            result.readMode = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('readMode'), owner);
        }
        if (configs.has('batchSize')) {
            handledConfigs.push('batchSize');
            result.batchSize = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('batchSize'),
                owner,
                IntegerDataFlowType.TYPE
            );
        }
        if (configs.has('httpRequestTimeout')) {
            handledConfigs.push('httpRequestTimeout');
            result.timeout = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('httpRequestTimeout'),
                owner,
                IntegerDataFlowType.TYPE
            );
        }
        if (configs.has('keys')) {
            result.sapODataKeyColumns = DataFlowExpressionUtils.unwrapExpressionArrayConfigValue(
                configs.get('keys'),
                owner
            );
        }
        return result;
    }
}
